import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Image from '@atlaskit/image';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { expVal } from '@confluence/feature-experiments';

import AudioBriefingChangeboardingGifLight from '../../assets/AudioBriefingChangeboardingGifLight.gif';
import AudioBriefingChangeboardingGifDark from '../../assets/AudioBriefingChangeboardingGifDark.gif';

const i18n = defineMessages({
	audioBriefingChangeboardingHeader: {
		id: 'audio.briefing-popup.changeboarding-header',
		description: 'Header for introducing the audio briefings feature',
		defaultMessage: 'Audio Briefings - New!',
	},
	audioChangeboardingHeader: {
		id: 'audio.briefing-popup.changeboarding-header',
		description: 'Header for introducing the audio briefings and narrations',
		defaultMessage: 'Audio - New!',
	},
	audioBriefingChangeboardingBody: {
		id: 'audio.briefing-popup.changeboarding-body',
		description: 'Description for audio briefings',
		defaultMessage:
			'Get up to speed on everything related to this page by listening to an Rovo-generated overview.',
	},
	audioChangeboardingBody: {
		id: 'audio.briefing-popup.changeboarding-body',
		description: 'Description for audio briefings and narrations',
		defaultMessage:
			'Listen to Rovo’s overview of everything related to this page or hear this page word-for-word.',
	},
	audioBriefingChangeboardingListenButton: {
		id: 'audio.briefing-popup.changeboarding.button.listen',
		description:
			'Text for button that allows users to acknowledge audio briefing changeboarding and listen to the briefing',
		defaultMessage: 'Listen',
	},
	audioBriefingChangeboardingDismissButton: {
		id: 'audio.briefing-popup.changeboarding.button.dismiss',
		description: 'Text for button that allows users to dismiss audio briefing changeboarding',
		defaultMessage: 'Dismiss',
	},
});

type AudioBriefingChangeboardingProps = {
	closePopup: () => void;
	handleFetchContent: () => void;
	onCloseChangeboarding: () => void;
};

export const AudioBriefingChangeboarding: FC<AudioBriefingChangeboardingProps> = ({
	closePopup,
	handleFetchContent,
	onCloseChangeboarding,
}) => {
	const { formatMessage } = useIntl();

	const { SpotlightCard } = useChoreographerAssets();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const headerTextContent = expVal('confluence_audio_narrations', 'isEnabled', false)
		? formatMessage(i18n.audioChangeboardingHeader)
		: formatMessage(i18n.audioBriefingChangeboardingHeader);

	const bodyTextContent = expVal('confluence_audio_narrations', 'isEnabled', false)
		? formatMessage(i18n.audioChangeboardingBody)
		: formatMessage(i18n.audioBriefingChangeboardingBody);

	const handleDismissChangeboarding = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioBriefingChangeboardingDismiss',
				source: 'audioBriefingChangeboarding',
			},
		}).fire();
		onCloseChangeboarding();
		closePopup();
	};

	const handleListen = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioBriefingChangeboardingListenNow',
				source: 'audioBriefingChangeboarding',
			},
		}).fire();
		onCloseChangeboarding();
		handleFetchContent();
	};

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'audioBriefingChangeboarding',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<SpotlightCard
			actions={[
				{
					onClick: handleListen,
					text: formatMessage(i18n.audioBriefingChangeboardingListenButton),
				},
				{
					onClick: handleDismissChangeboarding,
					appearance: 'subtle',
					text: formatMessage(i18n.audioBriefingChangeboardingDismissButton),
				},
			]}
			heading={headerTextContent}
			width={275}
			image={
				<Image
					src={AudioBriefingChangeboardingGifLight}
					srcDark={AudioBriefingChangeboardingGifDark}
					alt=""
				/>
			}
		>
			{bodyTextContent}
		</SpotlightCard>
	);
};
